import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useEffect, useId, useRef, } from 'react'

import { FocusTransitionManager, FocusTrap, IconButton } from 'slab'

import { Confirmation } from './Confirmation'
import { OpenEnded } from './OpenEnded'
import { Rating } from './Rating'

interface InProductFeedbackProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
  onDismiss?: () => Promise<void> | void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  title: React.ReactNode;
}

export function InProductFeedback ({
  children,
  defaultOpen = false,
  onOpenChange,
  open: openProp,
  onDismiss,
  title
}: InProductFeedbackProps) {
  const [open, setOpen] = useControllableState({
    defaultProp: defaultOpen,
    onChange: onOpenChange,
    prop: openProp,
  })

  const titleId = useId()
  const ref = useRef<HTMLDivElement>(null)

  const dismiss = useCallback(async () => {
    await onDismiss?.()
    setOpen(false)
  }, [setOpen, onDismiss])

  useEffect(() => {
    if (open) {
      function handleKeyDown (event: KeyboardEvent) {
        if (event.key === 'Escape') {
          if (ref.current?.contains(event.target as Node)) {
            dismiss()
          }
        }
      }

      window.addEventListener('keydown', handleKeyDown)

      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [open, onDismiss])

  return (
    <AnimatePresence>
      {open && (
        <FocusTransitionManager asChild>
          <FocusTrap aria-labelledby={titleId} setInitialFocusOn='h2' role='dialog'>
            <motion.div
              className='rounded-lg shadow p-3 bg-white'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{
                duration: 0.5,
                type: 'spring',
                bounce: 0.5,
              }}
              ref={ref}
            >
              <div className='flex items-center justify-between mb-3'>
                <h2
                  className='body-bold outline-none'
                  id={titleId}
                  tabIndex={-1}
                >
                  {title}
                </h2>
                <div className='flex justify-center items-center h-5 w-5'>
                  <IconButton
                    onClick={() => {
                      dismiss()
                    }}
                    icon={XIcon}
                    label='Dismiss'
                  />
                </div>
              </div>
              {children}
            </motion.div>
          </FocusTrap>
        </FocusTransitionManager>
      )}
    </AnimatePresence>
  )
}

InProductFeedback.Confirmation = Confirmation
InProductFeedback.OpenEnded = OpenEnded
InProductFeedback.Rating = Rating
